import React, { useEffect, useState, useRef } from "react"
import { Button, OverlayTrigger, Popover, ProgressBar } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import Icon from "../common/icon/icon.component"
import BulkOperation from "../state/bulk-operation/BulkOperation"
import BulkOperationService from "../state/bulk-operation/BulkService"
import EventBus from "../common/EventBus.js"
import { isError } from "../state/helpers/Error"
import { setBulkOperationsStatus } from "../state/bulk-operation/BulkOperationStatusActions"
import { getBulkOperationsStatus } from "../state/bulk-operation/BulkOperationStatusSelector"
import IntlProps from "../state/helpers/IntlProps"
import { connect, ConnectedProps } from "react-redux"
import { STATUS_BULKS_IN_PROGRESS, STATUS_BULKS_COMPLETED } from "../state/bulk-operation/BulkOperationsStatus"
import {
    BULK_OPERATION_MESSAGES,
    BULK_OPERATION_STATUS,
    BULK_OPERATION_STATUS_MESSAGES
} from "../utils/automation/bulk"

type OwnProps = {
    onChange: () => void
    isShopify: boolean
}

const NUMBER_MIN_AFTER_COMPLETED = 1
const SMALL_SCREEN_SIZE = 767

const requiresCsv = [
    "account-statements-creation"
]

const BulkFeedbackComponent = (props: Props) => {
    const [bulkOperations, setBulkOperations] = useState<null | BulkOperation[]>([])
    const buttonRef = useRef<HTMLButtonElement>(null)
    let intervalId: NodeJS.Timeout

    useEffect(() => {
        openFeedback(true)
    }, [])

    useEffect(() => {
        openFeedback(false)
    }, [props.bulkOperationsStatus])

    useEffect(() => {
        fetchData()
        return () => clearInterval(intervalId)
    }, [bulkOperations])

    const fetchData = () => {
        if (!intervalId) {
            intervalId = setInterval(async () => {
                const res = await getBulkOperations()
                setBulkOperations(res)
                if (res.length == 0) {
                    props.setBulkOperationsStatus(STATUS_BULKS_COMPLETED)
                    EventBus.dispatch(EventBus.TopicSendSuccessNotification, {
                        message: <FormattedMessage
                            id="bulk.modal.operations.operation_bulk_completed_message"
                            defaultMessage="All your bulk operations were completed."
                        />
                    })
                }
            }, 2000)
        }

        if (props.bulkOperationsStatus == STATUS_BULKS_COMPLETED) {
            clearInterval(intervalId)
        }

    }

    const openFeedback = (isRefresh) => {
        const load = async () => {
            const res = await getBulkOperations()
            if (res.length != 0) {
                if (isRefresh) {
                    props.setBulkOperationsStatus(STATUS_BULKS_IN_PROGRESS)
                } else {
                    setBulkOperations(res)
                }
                if (window.innerWidth > SMALL_SCREEN_SIZE) {
                    buttonRef.current?.click()
                }
            }
        }
        load()
    }

    const minuteDifference = (endDate: Date) => {
        let diff = secondDifference(endDate)
        diff /= 60
        return Math.abs(Math.round(diff))
    }

    const secondDifference = (endDate: Date) => {
        const today = new Date()
        const diff = (endDate.getTime() - today.getTime()) / 1000
        return diff
    }

    const getBulkOperations = async () => {
        const res = await BulkOperationService.GetPendingBulkOperationsByUser()
        if (!isError(res)) {
            const resultsIds = res.map(bulk => bulk.getId())
            let results = res ? res : []
            let completedOp = bulkOperations ? bulkOperations.filter(bulk => !resultsIds.includes(bulk.getId())) : []
            completedOp = completedOp.map(bulk => {
                getCompletedBulkResult(bulk)
                return bulk
            })
            results = results.concat(completedOp)
            results = results.filter(bulk => bulk.isInProgress() ||
                minuteDifference(bulk.getBulkOperationUpdatedAt()) < NUMBER_MIN_AFTER_COMPLETED)
            return results
        }
        return []
    }

    const getCompletedBulkResult = async (bulk: BulkOperation) => {
        if ((!bulk.getBulkOperationUrl() && requiresCsv.includes(bulk.getBulkOperationType())) ||
            bulk.getBulkOperationStatus() != BULK_OPERATION_STATUS.COMPLETED) {
            const bulkResult = await BulkOperationService.RetrieveBulkOperation(bulk.getId())
            if (!isError(bulkResult)) {
                const updatedBulk = bulkResult as BulkOperation
                bulk.setBulkOperationStatus(updatedBulk.getBulkOperationStatus())
                bulk.setBulkOperationUrl(updatedBulk.getBulkOperationUrl())
            }
        }

    }

    const getPopoverBulkProgress = () => {
        return <Popover id="popover-trigger-hover-focus" title="Popover bottom" style={{width: "300px"}}>
            <div style={{maxHeight: "600px", overflowY: "scroll"}}>
                {bulkOperations?.length != 0 && bulkOperations?.map((bulk, i) => {
                    return <div key={i} className="m-3 d-flex flex-column">
                        <strong>{props.intl.formatMessage(BULK_OPERATION_MESSAGES[bulk.getBulkOperationType()])}</strong>

                        <strong>
                            <small>{props.intl.formatMessage(BULK_OPERATION_STATUS_MESSAGES[bulk.getBulkOperationStatus()])}</small>
                        </strong>

                        {bulk.isInProgress() &&
                            <ProgressBar animated now={bulk.getPercentageOfProgress() == 0 ? 1 : bulk.getPercentageOfProgress()}>
                            </ProgressBar>
                        }

                        {bulk.isTerminated() &&
                            <div><small>
                                <FormattedMessage
                                    id="bulk_operation.feedback.component"
                                    defaultMessage="Your bulk operation was terminated."
                                />
                            </small></div>}

                        {bulk.isCompleted() && <div>
                            {bulk.getBulkOperationUrl() && <small>
                                <a href={bulk.getBulkOperationUrl()}>
                                    <FormattedMessage
                                        id="bulk_operation.feedback.download_pdf_link"
                                        defaultMessage="Download pdf"
                                    />
                                </a>
                            </small>}
                            <Icon value={"check"}/>
                        </div>}
                    </div>
                })}

                {bulkOperations?.length == 0 && <strong>
                    <FormattedMessage
                        id="bulk_operation.feedback.component_not_available"
                        defaultMessage="No bulk operations available."
                    />
                </strong>}
            </div>
        </Popover>
    }

    return (
        <div className={props.isShopify ? "" : "d-none d-md-block"}>
            {props.bulkOperationsStatus && props.bulkOperationsStatus == STATUS_BULKS_IN_PROGRESS &&
            <>
                <OverlayTrigger
                    trigger={["click"]}
                    placement="bottom"
                    overlay={getPopoverBulkProgress()}
                >
                    <Button size="sm" style={{height: "15px", paddingTop: "0px", paddingBottom: "0px"}} ref={buttonRef}>
                        <Icon value="sync" className="align-top h-75" size="10px" marginBottom="5px"/>
                    </Button>
                </OverlayTrigger>

            </>
            }
        </div>
    )
}

const mapState = (state, props: OwnProps & IntlProps) => {
    return {
        ...props,
        bulkOperationsStatus: getBulkOperationsStatus(state),
    }
}

const mapDispatch = {setBulkOperationsStatus}
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(BulkFeedbackComponent))


// WEBPACK FOOTER //
// ./src/bulk/BulkFeedbackComponent.tsx